import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Offset-paged list for the upcoming / review content panels.
 *
 * `fetchPage(offset)` must resolve to `{ list, size }` (or throw). Page 0
 * replaces the list, later pages append. Items persist across open/close so a
 * reopen paints the previous list instantly and only dims while page 0
 * refreshes; `reset()` (call it from the modal's onAfterClose) drops the
 * pagination cursor so the next open starts from page 0 again instead of
 * appending page N+1 onto a stale list.
 *
 * Every request carries an id and checks it after awaiting, so a response
 * that arrives late (or after unmount) is dropped rather than applied.
 */

const EMPTY = Object.freeze([])

export const assertOk = (resp) => {
    if (resp?.status !== 200) throw new Error(`Unexpected status ${resp?.status}`)
    return resp.data
}

const isSameData = (a, b) => {
    if (a === b) return true
    try {
        return JSON.stringify(a) === JSON.stringify(b)
    } catch (err) {
        return false
    }
}

export default function usePagedContentList({ fetchPage, enabled = true }) {
    const [items, setItems] = useState(EMPTY)
    const [inFlight, setInFlight] = useState(false)
    const [loadedOnce, setLoadedOnce] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [version, setVersion] = useState(0)
    const requestRef = useRef(0)
    const mountedRef = useRef(true)
    const fetchRef = useRef(fetchPage)
    fetchRef.current = fetchPage
    const itemsRef = useRef(items)
    itemsRef.current = items

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    const load = useCallback(async (offset) => {
        const requestId = ++requestRef.current
        const isCurrent = () => mountedRef.current && requestId === requestRef.current
        setInFlight(true)
        try {
            const page = await fetchRef.current(offset)
            if (!isCurrent()) return
            const list = Array.isArray(page?.list) ? page.list : EMPTY
            const size = Number(page?.size) || 0
            if (offset === 0) {
                if (!isSameData(itemsRef.current, list)) {
                    setItems(list)
                    setVersion((v) => v + 1)
                }
                setHasMore(list.length > 0 && size > list.length)
            } else {
                const merged = [...itemsRef.current, ...list]
                setItems(merged)
                setHasMore(list.length > 0 && size > merged.length)
            }
        } catch (err) {
            if (!isCurrent()) return
            console.error('usePagedContentList: load failed', err)
            // Keep whatever is on screen; just stop asking for more.
            setHasMore(false)
        } finally {
            if (isCurrent()) {
                setInFlight(false)
                setLoadedOnce(true)
            }
        }
    }, [])

    useEffect(() => {
        if (enabled) load(0)
    }, [enabled, load])

    const loadMore = useCallback(() => load(itemsRef.current.length), [load])
    const reload = useCallback(() => load(0), [load])
    const reset = useCallback(() => {
        // Invalidate anything still in flight so it cannot append after close.
        requestRef.current += 1
        setInFlight(false)
        setHasMore(false)
    }, [])

    const loading = enabled && (!loadedOnce || (inFlight && items.length === 0))
    const refreshing = inFlight && items.length > 0

    return { items, loading, refreshing, hasMore, version, loadMore, reload, reset }
}
